import { useParams, Link } from 'react-router-dom';
import { usePageMeta, useSchemaOrg } from '@/hooks/usePageMeta';
import PageShell from '@/components/feature/PageShell';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const relatedArticles = article
    ? articles.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 2)
    : [];

  usePageMeta({
    title: article ? `${article.title} | 蜜桃知识 | 美丽的小蜜桃` : '文章未找到 | 美丽的小蜜桃',
    description: article ? article.summary : '您访问的文章不存在或已被移除',
    keywords: article ? article.tags.join(',') : '',
    canonicalUrl: `https://meilixiaomitao.com/article/${id || ''}`,
    ogType: 'article',
    noindex: !article,
  });

  useSchemaOrg(article ? {
    organization: true,
    website: true,
    webpage: true,
    article: {
      headline: article.title,
      description: article.summary,
      datePublished: article.publishDate,
      author: article.author,
      image: article.image,
    },
    breadcrumb: [
      { name: '首页', url: 'https://meilixiaomitao.com/' },
      { name: '蜜桃知识', url: 'https://meilixiaomitao.com/articles' },
      { name: article.title },
    ],
  } : { webpage: true });

  if (!article) {
    return (
      <PageShell>
        <div className="pt-24 pb-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center py-20">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 text-sm mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回文章列表
            </Link>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <article className="pt-24 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '蜜桃知识', path: '/articles' },
                { label: article.title },
              ]}
            />
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
              <span className="text-xs text-foreground-400">·</span>
              <span className="text-xs text-foreground-400">{article.author}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5 leading-tight">
              {article.title}
            </h1>
          </div>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover rounded-lg"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_h4]:font-heading [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-foreground-900 [&_h4]:mt-8 [&_h4]:mb-3
              [&_p]:mb-4 [&_p]:text-foreground-700
              [&_strong]:text-foreground-900
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:text-foreground-700
              [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-4 [&_ol]:text-foreground-700
              [&_li]:mb-1"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-8 border-t border-background-200">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-foreground-400">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-14">
              <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} {...rel} />
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 text-center">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-600 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-all duration-300 cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </div>
              返回文章列表
            </Link>
          </div>
        </div>
      </article>
    </PageShell>
  );
}